import {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
    useMemo,
} from 'react'
import { useDispatch } from 'react-redux'

import { getNotifications, removeNotification } from 'background/services'
import {
    NotificationsT,
    SUPPORTED_NOTIFICATION_TYPES,
} from 'types/notifications'

import { AppContext } from 'app/AppProvider'

export const NotificationsContext = createContext<{
    notifications: NotificationsT
    clearNotifications: Dispatch<SetStateAction<{}>>
    clearNotification: (id: string) => {}
}>({
    notifications: [],
    clearNotifications: () => {},
    clearNotification: id => ({}),
})

export const NotificationsProvider = ({ children }) => {
    const dispatch = useDispatch()

    const [notifications, setNotifications] = useState<NotificationsT>([])

    const setFilteredNotifications = useMemo(
        () => (notifications: NotificationsT) =>
            setNotifications(
                notifications.filter(({ type }) =>
                    SUPPORTED_NOTIFICATION_TYPES.includes(type)
                )
            ),
        []
    )

    const clearNotifications = async () => {
        if (!notifications.length) {
            return
        }

        await removeNotification(null, { uid: notifications[0].uid })
    }

    const clearNotification = async id => {
        await removeNotification(null, { id })
    }

    const sortByDate = (notifications: NotificationsT) =>
        notifications.sort((a, b) => b.dateCreated - a.dateCreated)

    const { firstInstall, openPlaylistMode, openPlaybookMode, setPlaybookId } =
        useContext(AppContext)

    useEffect(() => {
        getNotifications(
            notifications =>
                setFilteredNotifications(sortByDate(notifications)),
            {}
        )
    }, [dispatch, setFilteredNotifications])

    const disableFirstInstall = firstInstall.setFalse

    // We have a scenario when we want to share a playbook/playlist with a new user. User will get an email with a link to install ext.
    // After installation and first open, he will receive a hidden notification with type: 'newUser', which contains playbookId and isPlaylist properties.
    // In this case we should open current playbook/playlist (from notification) and ignore firstInstall flow.
    useEffect(() => {
        if (!notifications.length) {
            return
        }
        // get 'newUser' notification
        const newUserNotification = notifications.find(
            e => e?.type === 'newUser' && e?.status === 'new'
        )

        if (!newUserNotification) {
            return
        }
        // disable firstInstall flow
        disableFirstInstall()
        // we don't need to show 'newUser' as a common notification
        const filteredNotifications = notifications.filter(
            e => e.type !== 'newUser'
        )
        setFilteredNotifications(sortByDate(filteredNotifications))

        // user invited via regular invite or installed ext by himself also receives 'newUser' but with { playbookId: null }
        if (newUserNotification.playbook?.id) {
            // get playbook from notification and run play mode
            // @TODO Update logic of go&watch to send the whole playbook obj, not just id
            setPlaybookId(newUserNotification.playbook.id)
            if (newUserNotification.playbook.isPlaylist) {
                openPlaylistMode()
            } else {
                openPlaybookMode()
            }
        }

        // Remove 'newUser' as we don't need it anymore
        removeNotification(() => {}, {
            id: newUserNotification.id,
        })
    }, [
        notifications,
        openPlaybookMode,
        openPlaylistMode,
        setPlaybookId,
        disableFirstInstall,
        setFilteredNotifications,
    ])

    const contextValue = {
        notifications,
        clearNotifications,
        clearNotification,
    }

    return (
        <NotificationsContext.Provider value={contextValue}>
            {children}
        </NotificationsContext.Provider>
    )
}
